"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { SitePlanEditor, SitePlanPrint, useSitePlan } from "@/components/tools/SitePlanCanvas";
import {
  Field,
  TextInput,
  TextArea,
  CheckboxRow,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
} from "@/components/tools/ui";

/* Site traffic management plan (workplace transport on a construction/works site
   — not Chapter 8 street works). Grounding: CDM 2015 regs 27/28 require defined
   traffic routes and physical separation of pedestrians from moving plant where
   practicable; HSE construction vehicle pages treat segregation of pedestrians
   from dumpers/telehandlers etc. as a legal duty, not a recommendation. The
   guiding principle throughout is "segregate, don't manage" — keep people and
   vehicles physically apart rather than relying on people watching out for
   each other. Reversing is the single highest-risk vehicle movement on site. */

const VEHICLE_TYPES: { id: string; label: string }[] = [
  { id: "delivery-lorries", label: "Delivery lorries / HGVs" },
  { id: "telehandler", label: "Telehandler" },
  { id: "excavator", label: "Excavator (360° / tracked)" },
  { id: "dumper", label: "Dumper" },
  { id: "concrete-wagons", label: "Concrete wagons" },
  { id: "vans", label: "Vans / light vehicles" },
  { id: "mobile-crane", label: "Mobile crane" },
];

interface Check {
  id: string;
  label: string;
  hint?: string;
}

const SEGREGATION: Check[] = [
  { id: "seg-walkways", label: "Pedestrian routes physically separated from vehicle routes", hint: "Barriers/fencing, not just painted lines — segregate, don't manage." },
  { id: "seg-barriers", label: "Walkways protected by barriers where they run alongside vehicle routes" },
  { id: "seg-crossings", label: "Designated, signed crossing points where routes must meet" },
  { id: "seg-gates", label: "Separate gates / entrances for people and for vehicles" },
  { id: "seg-signage", label: "Routes, crossings and one-way directions clearly signed" },
  { id: "seg-lighting", label: "Routes adequately lit for the hours worked" },
];

const REVERSING: Check[] = [
  { id: "rev-marshal", label: "Trained banksman / vehicle marshal where reversing is needed", hint: "Marshal kept in a safe position and high-vis, in the driver's view at all times." },
  { id: "rev-minimise", label: "Reversing minimised by one-way system or drive-through / turning area", hint: "Designing out reversing is more reliable than supervising it." },
  { id: "rev-aids", label: "Reversing aids fitted — cameras, sensors and audible alarms" },
  { id: "rev-exclusion", label: "Exclusion zone behind/around reversing and slewing plant, kept clear of people" },
  { id: "rev-comms", label: "Agreed signals between driver and marshal; driver stops if marshal lost from view" },
];

const DELIVERIES: Check[] = [
  { id: "del-route", label: "Delivery route and booked delivery times defined" },
  { id: "del-holding", label: "Holding / waiting area for arriving vehicles, off pedestrian routes" },
  { id: "del-unloading", label: "Unloading / loading zone sited away from pedestrians and walkways" },
  { id: "del-cab", label: "Driver rules: stay in cab during unloading / no unauthorised help", hint: "Drivers kept clear of lifting and slewing; no helping the crane or telehandler." },
  { id: "del-induction", label: "Visiting drivers briefed on the site rules and route on arrival" },
];

const RULES: Check[] = [
  { id: "rule-speed", label: "Site speed limit set and signed" },
  { id: "rule-parking", label: "Designated parking area; no parking on routes or near crossings" },
  { id: "rule-keys", label: "Keys-out / immobilise policy when plant is left unattended" },
  { id: "rule-hivis", label: "Hi-vis worn by everyone on or near vehicle routes at all times" },
  { id: "rule-foot", label: "Walk-the-walkways rule enforced — no shortcuts across vehicle routes", hint: "Bypassing a barrier is how segregation quietly stops working — monitor and enforce." },
];

const PUBLIC: Check[] = [
  { id: "pub-footpath", label: "Public footpath closures / diversions in place with signage", hint: "Liaise with the highway authority where the public highway or footway is affected." },
  { id: "pub-school", label: "Deliveries timed to avoid school-run / peak pedestrian periods where relevant" },
  { id: "pub-gates", label: "Site gates manned during vehicle movements and locked otherwise" },
  { id: "pub-sightlines", label: "Sightlines / mirrors at the access so drivers can see passing pedestrians" },
];

const PLANT: Check[] = [
  { id: "pl-quickhitch", label: "Quick-hitch checks: correct coupling confirmed before use of attachments" },
  { id: "pl-seatbelts", label: "Seatbelts worn in all ride-on plant (rollover protection only works belted in)" },
  { id: "pl-slew", label: "Exclusion zones around slewing plant — barriered to the slew radius" },
  { id: "pl-overhead", label: "Overhead services and height restrictions identified and signed on routes", hint: "Goalposts / bunting for overhead lines; route heights checked for high vehicles." },
  { id: "pl-ground", label: "Routes and set-up areas assessed for ground bearing / edges / excavations" },
];

const SECTIONS: { key: string; heading: string; intro: string; checks: Check[] }[] = [
  { key: "seg", heading: "Segregation", intro: "Keeping pedestrians physically apart from vehicles is the single most effective control. Aim to design out the conflict before relying on signage or supervision.", checks: SEGREGATION },
  { key: "rev", heading: "Reversing controls", intro: "Reversing is the highest-risk vehicle movement on site. Remove the need to reverse first; only then control the reversing that remains.", checks: REVERSING },
  { key: "del", heading: "Deliveries", intro: "Plan where vehicles arrive, wait and unload so that loading never happens over a pedestrian route.", checks: DELIVERIES },
  { key: "rule", heading: "Speed & site rules", intro: "Set the everyday rules that keep routes safe between deliveries.", checks: RULES },
  { key: "pub", heading: "Public interface", intro: "Where the site meets the public — footpaths, neighbours, the access onto the highway.", checks: PUBLIC },
  { key: "pl", heading: "Plant-specific controls", intro: "Hazards tied to the machines themselves rather than the routes they travel.", checks: PLANT },
];

export function TrafficManagementPlan() {
  const [site, setSite] = useState("");
  const [coordinator, setCoordinator] = useState("");
  const [vehicles, setVehicles] = useState<Record<string, boolean>>({});
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [deliveryDetail, setDeliveryDetail] = useState("");
  const [speedLimit, setSpeedLimit] = useState("");
  const [sketchNote, setSketchNote] = useState("");
  const [sitePlan, setSitePlan] = useSitePlan("traffic-management-plan");
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();
  const resultTracked = useRef(false);

  const selectedVehicles = useMemo(
    () => VEHICLE_TYPES.filter((v) => vehicles[v.id]),
    [vehicles]
  );

  // Fire the funnel event once per visit when the plan first has real content.
  useEffect(() => {
    if (!resultTracked.current && selectedVehicles.length > 0) {
      resultTracked.current = true;
      trackToolResult("traffic-management-plan", { vehicles: selectedVehicles.length });
    }
  }, [selectedVehicles.length]);

  const toggleVehicle = (id: string) =>
    setVehicles((v) => ({ ...v, [id]: !v[id] }));
  const toggleCheck = (id: string, val: boolean) =>
    setChecks((c) => ({ ...c, [id]: val }));

  const hasContent = selectedVehicles.length > 0 || site.trim() !== "";

  // Minimum content for a usable plan — drives the progress panel + print guard.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Site / project", done: site.trim().length > 0 },
    { label: "Traffic management coordinator", done: coordinator.trim().length > 0 },
    { label: "Plant & vehicle types on site", done: selectedVehicles.length > 0 },
    { label: "Delivery route, times & arrangements", done: deliveryDetail.trim().length > 0 },
    { label: "Site speed limit", done: speedLimit.trim().length > 0 },
    { label: "Site plan (sketch it below or describe the attached drawing)", done: sitePlan.length > 0 || sketchNote.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
      {/* Inputs */}
      <div className="space-y-6">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Site &amp; coordinator</h2>
          <p className="mt-1 text-sm text-muted">
            Who the plan is for and what moves around the site. Tick every vehicle and item of plant that will
            be on site — each one shapes the segregation and reversing controls below.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Site / project">
              <TextInput value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. Plot 4, Mill Lane" />
            </Field>
            <Field label="Traffic management coordinator">
              <TextInput value={coordinator} onChange={(e) => setCoordinator(e.target.value)} placeholder="Name / role" />
            </Field>
          </div>
          <div className="mt-5">
            <span className="mb-2 block text-[0.85rem] font-semibold text-ink">Plant &amp; vehicle types on site</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {VEHICLE_TYPES.map((v) => (
                <CheckboxRow key={v.id} checked={!!vehicles[v.id]} onChange={() => toggleVehicle(v.id)}>
                  {v.label}
                </CheckboxRow>
              ))}
            </div>
          </div>
        </div>

        {SECTIONS.map((section) => (
          <div key={section.key} className="card shadow-soft p-6">
            <h2 className="text-xl">{section.heading}</h2>
            <p className="mt-1 text-sm text-muted">{section.intro}</p>
            <div className="mt-4 space-y-2">
              {section.checks.map((c) => (
                <CheckboxRow key={c.id} checked={!!checks[c.id]} onChange={(val) => toggleCheck(c.id, val)}>
                  {c.label}
                  {c.hint && <span className="mt-0.5 block text-[0.8rem] text-muted">{c.hint}</span>}
                </CheckboxRow>
              ))}
            </div>

            {section.key === "del" && (
              <div className="mt-4">
                <Field label="Delivery route, times & arrangements" hint="Approach road, gate used, booking/banned times, holding and unloading areas.">
                  <TextArea
                    value={deliveryDetail}
                    onChange={(e) => setDeliveryDetail(e.target.value)}
                    placeholder="e.g. Deliveries via north gate off Mill Lane, 08:30–15:30 only (avoiding school run). Hold in lay-by until called; unload in barriered zone at car park, away from the walkway."
                  />
                </Field>
              </div>
            )}
            {section.key === "rule" && (
              <div className="mt-4">
                <Field label="Site speed limit">
                  <TextInput value={speedLimit} onChange={(e) => setSpeedLimit(e.target.value)} placeholder="e.g. 5 mph" />
                </Field>
              </div>
            )}
          </div>
        ))}

        <SitePlanEditor
          value={sitePlan}
          onChange={setSitePlan}
          defaultPreset="traffic"
          title="Site plan sketch"
          hint="Draw the routes and zones a traffic plan needs: vehicle and pedestrian routes, crossing points, gates, exclusion zones, holding and unloading areas. The sketch prints with the plan."
        />

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Plan notes</h2>
          <p className="mt-1 text-sm text-muted">
            Anything the sketch can&apos;t show — one-way directions in words, booking arrangements, or what a
            separately attached scale drawing covers.
          </p>
          <div className="mt-4">
            <Field label="Notes on the plan" hint="Routes, one-way arrows, crossing points, gates, parking, exclusion zones, unloading and holding areas.">
              <TextArea
                value={sketchNote}
                onChange={(e) => setSketchNote(e.target.value)}
                placeholder="e.g. One-way loop clockwise; pedestrian walkway (barriered) along the east boundary to the welfare unit; turning head at the south end to remove reversing; crane exclusion zone hatched."
              />
            </Field>
          </div>
        </div>

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* Printable plan */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Plan completion" />
        </div>
        {hasContent ? (
          <>
            <div className="print-area card shadow-soft p-6" id="tmp-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Site traffic management plan
              </div>
              <h2 className="mt-1 text-lg">
                {site ? `${site} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>
              {coordinator && (
                <p className="mt-1 text-sm text-muted">Traffic management coordinator: {coordinator}</p>
              )}

              <div className="mt-4">
                <h3 className="text-[0.92rem] font-bold text-ink">Plant &amp; vehicles on site</h3>
                {selectedVehicles.length > 0 ? (
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {selectedVehicles.map((v) => (
                      <li key={v.id} className="rounded-full bg-mist px-2.5 py-1 text-[0.8rem] text-ink">
                        {v.label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-muted">None selected.</p>
                )}
              </div>

              {speedLimit && (
                <p className="mt-3 text-sm text-ink">
                  <span className="font-semibold">Site speed limit:</span> {speedLimit}
                </p>
              )}

              {SECTIONS.map((section) => {
                const ticked = section.checks.filter((c) => checks[c.id]);
                const extra =
                  section.key === "del" && deliveryDetail.trim()
                    ? deliveryDetail.trim()
                    : null;
                if (ticked.length === 0 && !extra) return null;
                return (
                  <div key={section.key} className="mt-4">
                    <h3 className="text-[0.92rem] font-bold text-ink">{section.heading}</h3>
                    {ticked.length > 0 && (
                      <ul className="mt-1.5 space-y-1 text-[0.86rem] leading-snug text-body">
                        {ticked.map((c) => (
                          <li key={c.id} className="flex gap-2">
                            <span aria-hidden className="text-emerald-deep">✓</span>
                            <span>{c.label}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {extra && (
                      <p className="mt-2 whitespace-pre-wrap text-[0.86rem] leading-snug text-body">{extra}</p>
                    )}
                  </div>
                );
              })}

              <div className="mt-5 rounded-xl border border-dashed border-line-2 bg-mist/60 p-4">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Attach site plan</div>
                <p className="mt-1 text-[0.86rem] leading-snug text-body">
                  Attach your marked-up site plan to this document — show exclusion zones, vehicle and pedestrian
                  routes, one-way directions, crossing points, gates, parking, and holding/unloading areas.
                </p>
                {sketchNote.trim() && (
                  <p className="mt-2 whitespace-pre-wrap text-[0.86rem] leading-snug text-ink">{sketchNote.trim()}</p>
                )}
              </div>

              <div className="mt-5 border-t border-line pt-3 text-[0.84rem] text-ink">
                <p className="text-muted">
                  This plan documents the traffic management arrangements for the site. A competent person must
                  confirm the arrangements are adequate for the actual site conditions, and the plan must be kept
                  under review as the site changes.
                </p>
              </div>

              <SitePlanPrint value={sitePlan} heading="Site traffic plan sketch" />

              <EvidencePrint value={evidence} briefing />

              <PrintFooter toolName="traffic management plan builder" />
            </div>
            <GuardedPrintButton
              tool="traffic-management-plan"
              label="Print / save plan as PDF"
              missing={missingRequired}
              warningTitle="This traffic management plan still has empty core sections"
            />
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Add a site name and tick the plant and vehicles on site to start building a printable traffic
            management plan.
          </div>
        )}
      </div>
    </div>
  );
}
